/**
 * Aviator — the rules, pure and deterministic. Plan §6.1 (docs/ADRENALINE_SHARED_TABLES_PLAN.md).
 *
 * THE CURVE IS THE CONTRACT. The server (phase D) and this file compute the
 * same m(t) to 2 dp, proven by scripts/assets/aviator_engine_test.mjs — the
 * player app never imports shared/src, so parity is a gate, not an import.
 *
 * X-DOCTRINE: every multiplier shown is TOTAL RETURN at 2 dp ("X2.35" = you
 * get 2.35× your stake). Nothing here ever says "to 1".
 */

/** growth constant: X2 at 6.3s, X10 at 20.9s, X500 at 56.5s */
export const GROWTH = 0.11;
/** the tail ends here (≈57s of flight) */
export const MAX_MULT = 500;
/** 3% of rounds crash on the runway (M = 1.00 — every bet loses) */
export const INSTANT_CRASH = 0.03;
/** Pareto tail exponent of the natural prior P(M ≥ x) = (1 − INSTANT_CRASH)·x^(−ALPHA) */
export const ALPHA = 1.10;

/** round pacing (ms) — the same numbers the server clock will publish */
export const WINDOW_MS = 6_000;      // betting open
export const ARM_MS = 1_000;         // locked → takeoff (engine rev theatre)
export const SETTLE_MS = 1_500;      // crash → result stamps land
export const INTERMISSION_MS = 3_000;

/** multiplier at t seconds after takeoff, quantised DOWN to 2 dp — the only
 *  form that ever reaches a wallet or a screen */
export function mAt(tSec: number): number {
  if (tSec <= 0) return 1;
  const raw = Math.exp(GROWTH * tSec);
  return Math.min(MAX_MULT, Math.floor(raw * 100 + 1e-9) / 100);
}

/** seconds of flight until the multiplier reaches M (the crash instant) */
export function tFor(M: number): number {
  return Math.log(Math.max(1, M)) / GROWTH;
}

/** what a cashout pays: floor(stake × m), rupees are integers */
export function payout(stake: number, m: number): number {
  return Math.floor(stake * m + 1e-9);
}

/** quantise any multiplier the way the wallet sees it */
export const q2 = (m: number) => Math.floor(m * 100 + 1e-9) / 100;

/** sample a crash point from the natural prior. rng ∈ [0,1). PHASE-2 SEAM:
 *  in production the server's scorer picks M from this prior's candidate
 *  grid; the client only ever learns M at crash. */
export function sampleCrash(rng: () => number): number {
  const u = rng();
  if (u >= 1 - INSTANT_CRASH) return 1;
  const M = Math.pow((1 - INSTANT_CRASH) / Math.max(u, 1e-12), 1 / ALPHA);
  /* 2-dp quantisation would fold M ∈ [1.00, 1.01) into the instant-crash
     bucket and silently raise it to ~4%; the non-instant branch therefore
     starts at X1.01 so P(M = 1.00) stays exactly INSTANT_CRASH */
  return Math.max(1.01, Math.min(MAX_MULT, q2(M)));
}

/** P(M ≥ x) under the natural prior — used by the gate and by the fake crowd */
export function survival(x: number): number {
  if (x <= 1) return 1;
  return (1 - INSTANT_CRASH) * Math.pow(x, -ALPHA);
}

/** history-chip colour band */
export type Band = 'low' | 'mid' | 'high';
export const bandOf = (M: number): Band => (M < 2 ? 'low' : M < 10 ? 'mid' : 'high');

/** "X2.35" — always 2 dp on this table (the readout must never jitter width) */
export function fmtX(m: number): string {
  return `X${q2(m).toFixed(2)}`;
}

/** the panel state machine, shared by the local and the server clock */
export type PanelState =
  | { kind: 'idle' }
  | { kind: 'placed'; stake: number; autoX: number | null }            // waiting for takeoff
  | { kind: 'queued'; stake: number; autoX: number | null }            // placed after lock → next round
  | { kind: 'flying'; stake: number; autoX: number | null }
  | { kind: 'cashed'; stake: number; m: number; won: number }
  | { kind: 'lost'; stake: number; M: number };
